import { useEffect } from "react";
import { useNavigate, useNavigationType } from "react-router-dom";
import { BRAND, BRAND_LIGHT} from "@/shared/constants.js";
import { STEP_PATHS } from "@/modules/Quote/constants.js";
import Alert from "@/shared/components/Alert.jsx";
import { ZIPCODE_NOT_AVAILABLE_MSG, toError } from "@/shared/utils/misc.js";
import { dateValid, fmtDate, formatDate } from "@/shared/utils/dateHelpers.js";
import { isValidZip } from "@/shared/utils/validators.js";
import { ilfDlfHasRequiredDefaults } from "@/modules/Quote/utils/ilfHelpers.js";
import {
  resetIlfDlfError,
  resetIlfDlfResponse,
  useIlfDlfError,
  useIlfDlfFetcher,
  useIlfDlfLoading,
} from "@/hooks/useIlfDlfFetcher.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";
import { useSpecialityId } from "@/hooks/useSpeciality.js";
import { Field } from "@/shared/components/Field.jsx";
import { TextInput } from "@/shared/components/TextInput.jsx";
import { SectionTitle } from "@/shared/components/SectionTitle.jsx";
import { Spacer } from "@/shared/components/Spacer.jsx";
import { ShieldIcon } from "@/shared/components/Icon.jsx";
import { btnOutline, btnPrimary, dis } from "@/shared/utils/styles.js";

import { useStore } from "@/shared/store/useStore.js";
import quoteFormStore from "@/modules/Quote/store/quoteFormStore.js";
import signupStore from "@/modules/Auth/store/signupStore.js";
import loginStore from "@/modules/Auth/store/loginStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";

export default function LocationDatePage() {
  const navigate     = useNavigate();
  const navType      = useNavigationType();

  // ── Store-backed reads ─────────────────────────────────────────────────
  const zip      = useStore(quoteFormStore, (s) => s.zip);
  const effDate  = useStore(quoteFormStore, (s) => s.effDate);
  const regEmail = useStore(signupStore, (s) => s.regEmail);
  const insuredProfile  = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);

  const specialityId  = useSpecialityId();
  const fetchIlfDlf   = useIlfDlfFetcher();
  const ilfDlfError   = useIlfDlfError();
  const ilfDlfLoading = useIlfDlfLoading();

  // ── Clear fields on fresh entry; preserve them when navigating back ───
  useEffect(() => {
    if (navType === "POP") return;
    quoteFormStore.zip = "";
    quoteFormStore.effDate = "";
  }, []);

  // ── Get-my-estimate: fetch ILF/DLF, then advance ──────────────────────
  const canSubmit = isValidZip(zip) && dateValid(effDate) && !ilfDlfLoading;

  // Fetch the ILF/DLF defaults, then advance to `step`. The quick estimate
  // lands on the soft-quote page (step 1); the "customize" path skips the
  // canned estimate and drops the user straight into the full application
  // (step 2) where specialty, practice, and coverage parameters are chosen.
  const runEstimate = async (step) => {
    if (!canSubmit) return;
    resetIlfDlfError();
    try {
      const data = await fetchIlfDlf({
        zipcode: zip,
        specialtiesid: specialityId,
        effectiveDate: fmtDate(effDate),
      });
      if (!ilfDlfHasRequiredDefaults(data)) {
        resetIlfDlfResponse();
        ilfDlfStore.ilfDlfError = toError(new Error(ZIPCODE_NOT_AVAILABLE_MSG));
        return;
      }
      submissionStore.step = step;
      navigate(STEP_PATHS[step]);
    } catch (_) {
      // Network/HTTP errors are already captured by fetchIlfDlf.
    }
  };

  const handleGetEstimate = () => runEstimate(1);
  const handleCustomize  = () => runEstimate(2);

  // Pressing Enter while in the effective-date field gets the estimate, so
  // the user can complete the two-field form without reaching for the mouse.
  const onEffDateKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    e.currentTarget.blur();
    handleGetEstimate();
  };

  // ── Render ─────────────────────────────────────────────────────────────
  const msg = ilfDlfError?.message || "";
  const isZipUnavailable =
    msg.toLowerCase() === ZIPCODE_NOT_AVAILABLE_MSG.toLowerCase()
    || msg.toLowerCase().includes("zip code not found");

  const onSignInLink = () => {
    if (regEmail.trim()) loginStore.loginEmail = regEmail.trim();
    navigate("/signin");
  };

  return (
    <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column" }}>
      <div style={{ textAlign: "center", margin: "10px 0 4px" }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: BRAND_LIGHT, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><ShieldIcon size={22} /></div>
      </div>
      <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 600, color: "#1a1a1a", textAlign: "center", margin: "8px 0 4px" }}>PA-malpractice coverage</h1>
      <p style={{ fontSize: 13, color: "#777", textAlign: "center", lineHeight: 1.5, margin: "0 0 20px" }}>Instant estimated quote. Just two fields.</p>

      <Field label="ZIP code">
        <TextInput
          value={zip}
          onChange={(v) => { quoteFormStore.zip = v.replace(/\D/g, "").slice(0, 5); }}
          placeholder="e.g. 92688"
          inputMode="numeric"
          maxLength={5}
          autoComplete="off"
        />
      </Field>
      <Field label="Policy effective date">
        <TextInput
          value={effDate}
          onChange={(v) => { quoteFormStore.effDate = formatDate(v); }}
          onKeyDown={onEffDateKeyDown}
          placeholder="MM/DD/YYYY"
          autoComplete="off"
        />
      </Field>

      <div style={{ borderTop: "1px solid #f0f0f0", margin: "0 0 16px" }} />

      <div style={{ opacity: 1, pointerEvents: "none" }}>
        <SectionTitle>Premium</SectionTitle>
        {[60, 80, 45].map((w, i) => <div key={i} style={{ background: "#eee", borderRadius: 6, height: 13, marginBottom: 7, width: `${w}%` }} />)}
        <SectionTitle>Coverage</SectionTitle>
        {[75, 55, 70].map((w, i) => <div key={i} style={{ background: "#eee", borderRadius: 6, height: 13, marginBottom: 7, width: `${w}%` }} />)}
      </div>

      <Spacer />

      {isZipUnavailable && (
        <div>
          <Alert type="error" message={ZIPCODE_NOT_AVAILABLE_MSG} className="alert-center" />
        </div>
      )}

      {ilfDlfError && !isZipUnavailable && (
        <div>
          <Alert type="error" className="alert-center">
            {msg || "Could not load coverage options for this zip. Please try again."}
          </Alert>
        </div>
      )}

      <button type="button" style={dis(btnPrimary, canSubmit)} onClick={handleGetEstimate}>
        {ilfDlfLoading ? "Loading…" : "Get my estimate"}
      </button>

      <button type="button" style={dis(btnOutline, canSubmit)} onClick={handleCustomize}>
        Customize coverage & limits
      </button>

      {!isAuthenticated && (
        <div style={{ textAlign: "center", padding: "8px 0 0" }}>
          <span style={{ fontSize: 12, color: "#999" }}>Already have an account? </span>
          <span onClick={onSignInLink} style={{ fontSize: 12, color: BRAND, fontWeight: 500, textDecoration: "underline", cursor: "pointer" }}>Sign in</span>
        </div>
      )}

      <div style={{ textAlign: "center", padding: "8px 0 2px" }}>
        <span style={{ fontSize: 10, color: "#aaa" }}>Coverage provided by Doctors Professional Liability RRG</span>
        <span style={{ fontSize: 10, color: "#ccc" }}> · </span>
        <span style={{ fontSize: 10, color: "#aaa", fontWeight: 500 }}>Demotech A-rated</span>
      </div>
    </div>
  );
}
